<template>
    <div class="disclaimer">
  <strong>Disclaimer:</strong> The generated plans are not fully accurate as this system is still a work in progress. 
  Updated and finalized plans will be available by <strong>February</strong>.
</div>
  <div class="container-fluid transfer two-to-four-year-transfer-container">
    <div class="row">
      <div class="col-md-12 text-center">
        <h2>Two year to four year transfer plan</h2>
      </div>
    </div>

    <div class="row two-to-four-year-form-container">
      <div class="row">
        <div class="col-md-6">
          <h5>Select your starting school</h5>
          <SearchSelect class="school-selection-search" :items="startingSchools" :labelKey="'name'"
            :placeholder="startingSchool ? startingSchool.name : 'Search Schools'"
            @item-selected="onSelectStartingSchool" />
        </div>

        <div class="col-md-6">
          <h5>Select program at your starting school</h5>
          <SearchSelect class="school-selection-search" :isLoading="isLoadingStartingPrograms"
            :items="startingSchoolPrograms" :labelKey="'display_name'"
            :placeholder="startingProgram ? startingProgram.display_name || startingProgram.name : 'Search Programs'"
            @item-selected="onSelectStartingProgram" />
        </div>
      </div>

      <div class="row">
        <div class="col-md-6">
          <h5>Select your destination school</h5>
          <SearchSelect class="school-selection-search" :items="destinationSchools" :labelKey="'name'"
            :placeholder="destinationSchool ? destinationSchool.name : 'Search Schools'"
            @item-selected="onSelectDestinationSchool" />
        </div>

        <div class="col-md-6">
          <h5>Select program at your destination school</h5>
          <SearchSelect class="school-selection-search" :isLoading="programsLoading" :items="destinationSchoolPrograms"
            :labelKey="'display_name'"
            :placeholder="destinationProgram ? (destinationProgram.display_name || destinationProgram.name) : 'Search Programs'"
            @item-selected="onSelectDestinationProgram" />
        </div>
      </div>

      <div class="row">
        <div class="col-md-12 text-center mt-5">
          <button class="btn btn-primary" type="button" @click="onGenerate"
            :disabled="!startingProgram || !destinationProgram || loading">
            Generate Transfer Plan
          </button>
        </div>
      </div>
    </div>

    <!-- Names row -->
    <div v-if="transferReportLoaded" class="school-names">
      <span class="school-name" :class="{ set: !!startingSchool }">
        <i class="fa-solid fa-school"></i>
        <span v-if="startingSchool">{{ startingSchool.name }}</span>
        <span v-else>Starting School</span>
      </span>
      <i class="fa-solid fa-arrow-right"></i>
      <span class="school-name" :class="{ set: !!destinationSchool }">
        <i class="fa-solid fa-school"></i>
        <span v-if="destinationSchool">{{ destinationSchool.name }}</span>
        <span v-else>Destination School</span>
      </span>
    </div>

    <!-- Plan viz -->
    <div v-if="transferReportLoaded" class="row two-to-four-year-report-container">
      <div class="col-md-12 pl-0">
        <button v-if="userIsLoggedIn" @click="saveTransferPlan" class="btn btn-success mb-2 mr-2 ml-0"
          :disabled="planSaved || isSavingPlan">
          <span class="button-caption" v-if="isSavingPlan">Saving Plan ...</span>
          <span class="button-caption" v-else-if="planSaved">Plan Saved</span>
          <span class="button-caption" v-else>Save to my Plans</span>
        </button>

        <button data-toggle="modal" data-target="#reportSummarModal" class="btn btn-primary mb-2" type="button">
          Analyze transfer plan
        </button>
      </div>

      <div class="col-md-12 dp-visualization-container p-4">
        <DegreePlanVisualization v-if="fourYearPlan && fourYearPlan.length" :planTermData="fourYearPlan"
          :isTransferPlan="true" :hadTransfer="true" :readOnly="true" />
      </div>
    </div>

    <ReportSummaryModal :reportData="transferPlanAnalysis" />

    <LoadingState v-if="loading" :message="loadingMsg" :progress="loadingProgress" :steps="loadingSteps"
      :currentStep="currentLoadingStep" />

    <ErrorState v-else-if="error" :message="errorMsg" :details="errorDetails" :canRetry="canRetry" @retry="onGenerate"
      @back="() => { }" @reset="resetError" />
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import SearchSelect from '@/components/SearchSelect.vue';
import ReportSummaryModal from '@/components/TransferSummaryModal.vue';
import LoadingState from '@/components/LoadingState.vue';
import ErrorState from '@/components/ErrorState.vue';
import type School from '@/models/school';
import type Program from '@/models/program';
import store from '@/store';
// @ts-ignore
import { DegreePlanVisualization } from '@unm-idi/vue-degree-plan/vue-degree-plan.js';

import { usePrograms } from '@/composables/usePrograms';
import { useLoadingError } from '@/composables/useLoadingError';
import { createTermObjects } from '@/utils/degreePlanUtils';
import { getSchools, generateTwoToFourPlan } from '@/services/apiService';

// ------------ auth ------------
const userIsLoggedIn = computed(() => store.state.userToken != null);

// ------------ composables ------------
const {
  programs: destPrograms,
  isLoading: programsLoading,
  getProgramsFromLocalNdjson,
  getProgramRequirement: getProgramReq,
} = usePrograms();

const {
  loading, loadingMsg, loadingProgress, currentLoadingStep, loadingSteps,
  error, errorMsg, errorDetails, canRetry,
  startLoadingWithProgress, updateLoadingProgress, completeLoading, handleError, resetError,
} = useLoadingError();

// ------------ state ------------
const startingSchools = ref<School[]>([]);
const destinationSchools = ref<School[]>([]);

const startingSchool = ref<School | null>(null);
const destinationSchool = ref<School | null>(null);

const startingSchoolPrograms = ref<Program[]>([]);
const destinationSchoolPrograms = computed(() => {
  const enabledCodes = ['ACCTBSBA', 'BIOLBS'];

  return destPrograms.value.map((p) => {
    const enabled = enabledCodes.includes(p.code);
    return { ...p, disable: false };
  });
});

const startingProgram = ref<Program | null>(null);
const destinationProgram = ref<Program | null>(null);

const transferPlanAnalysis = ref<any>({});
const transferReportLoaded = ref(false);
const fourYearPlan = ref<any[]>([]);
const planSaved = ref(false);
const isSavingPlan = ref(false);

// Only for this view:
const isLoadingStartingPrograms = ref(false);

// ------------ helpers ------------
// fetch programs for *any* school using university_layouts (used for starting school)
async function fetchProgramsForSchool(school: School, path = "/data/ActivePrograms.json"): Promise<Program[]> {
  try {
    // Load locally cached NDJSON file instead of calling the API
    let programs = await getProgramsFromLocalNdjson(school, path);

    // Disable only specific programs (SCIENCEAS)
    const disabledCodes = ['',];

    programs = programs.map((p) => {
      const disabled = disabledCodes.includes(p.code);
      return { ...p, disable: disabled };
    });

    console.log(
      `Loaded ${programs.length} programs from local NDJSON for ${school.name}`
    );
    return programs;
  } catch (e: any) {
    throw new Error(`Failed to load local programs for ${school.name}: ${e.message || e}`);
  }
}

// Merge source/dest into a single four-year flow
function mergeDegreeplanIntoFourYear(sourceTerms: any[], destTerms: any[]): any[] {
  const combined = [...(sourceTerms || []), ...(destTerms || [])].map((t, i) => {
    const term = JSON.parse(JSON.stringify(t));
    term.id = i;
    term.name = `Term ${i + 1}`;
    term.position = i;
    if (Array.isArray(term.plan_items)) {
      term.plan_items.forEach((pi: any) => {
        if (pi?.requirement?.parent_id !== undefined) {
          pi.requirement.parent_id = i;
        }
        if (pi?.requirement?.course?.academic_period) {
          const currentYear = new Date().getFullYear();
          pi.requirement.course.academic_period.id = i;
          pi.requirement.course.academic_period.term = term.name;
          pi.requirement.course.academic_period.code = `${currentYear}.${i}`;
          pi.requirement.course.academic_period.name = `${term.name} ${currentYear}`;
          pi.requirement.course.academic_period.academic_year = `${currentYear}-${currentYear + 1}`;
        }
      });
    }
    return term;
  });
  return combined;
}

// ------------ init ------------
(function init() {
  const { startingSchools: s, destinationSchools: d } = getSchools();
  startingSchools.value = s;
  destinationSchools.value = d;
})();

// ------------ actions ------------
async function onSelectStartingSchool(school: School) {
  try {
    resetError();
    transferReportLoaded.value = false;
    startingSchool.value = school;

    isLoadingStartingPrograms.value = true;
    startingSchoolPrograms.value = await fetchProgramsForSchool(school, "/data/pima-community-college/ActivePrograms.json")
  } catch (e: any) {
    handleError(e);
  } finally {
    isLoadingStartingPrograms.value = false;
  }
}

async function onSelectDestinationSchool(school: School) {
  try {
    resetError();
    transferReportLoaded.value = false;
    destinationSchool.value = school;
    await getProgramsFromLocalNdjson(school);
  } catch (e: any) {
    handleError(e);
  }
}

function onSelectStartingProgram(program: Program) {
  transferReportLoaded.value = false;
  startingProgram.value = program;
}

function onSelectDestinationProgram(program: Program) {
  transferReportLoaded.value = false;
  destinationProgram.value = program;
}

async function onGenerate() {
  if (!startingSchool.value || !destinationSchool.value || !startingProgram.value || !destinationProgram.value) return;

  startLoadingWithProgress();
  transferReportLoaded.value = false;
  planSaved.value = false;

  try {
    updateLoadingProgress(1, `Fetching requirements: ${destinationProgram.value.name}`);
    const destReq = await getProgramReq(destinationProgram.value);
    if (destReq && destinationProgram.value) {
      destinationProgram.value = { ...(destinationProgram.value as Program), requirements: destReq } as Program;

    }

    updateLoadingProgress(3, 'Generating two-to-four transfer plan...');
    const payload = {
      source_program: startingProgram.value,
      dest_program: destinationProgram.value,
    };

    const dataObj = await generateTwoToFourPlan(payload);

    updateLoadingProgress(5, 'Building degree plans...');
    const sourceTerms = createTermObjects(
      dataObj?.source_degreeplan || [],
      0
    );
    const destTerms = createTermObjects(
      dataObj?.destination_degreeplan || [],
      4
    );

    fourYearPlan.value = mergeDegreeplanIntoFourYear(sourceTerms, destTerms);
    transferPlanAnalysis.value = dataObj?.transfer_plan_report || {};
    transferReportLoaded.value = true;

    completeLoading();
  } catch (e: any) {
    handleError(e);
  }
}

async function saveTransferPlan() {
  try {
    isSavingPlan.value = true;
    const plan = {
      name: `${startingProgram.value?.name} - ${destinationProgram.value?.name}`,
      startingSchool: startingSchool.value,
      destinationSchool: destinationSchool.value,
      startingProgram: startingProgram.value,
      destinationProgram: destinationProgram.value,
      fourYearPlan: fourYearPlan.value,
      transferPlanAnalysis: transferPlanAnalysis.value,
    };
    await store.methods.uploadUserFile(
      plan,
      startingProgram.value?.name || 'Source',
      destinationProgram.value?.name || 'Destination'
    );
    planSaved.value = true;
  } catch (e: any) {
    handleError(e);
  } finally {
    isSavingPlan.value = false;
  }
}
</script>

<style>
.plan-term .term-head .term-title {
  font-family: "Roboto", sans-serif;
  font-weight: 700;
  font-size: 18px;
  color: #00205B;
  letter-spacing: 0.02em;
}

.plan-saved {
  color: green;
}

.two-to-four-year-transfer-container .dp-visualization-container {
  position: relative;
}
.disclaimer{
  background:#fff3cd; 
  border:1px solid #ffeeba; color:#856404; 
  padding:12px 16px; 
  border-radius:8px; 
  font-family:Arial, Helvetica, sans-serif;
  text-align: center;
}

.two-to-four-year-transfer-container .dp-visualization-container .loc-divider {
  z-index: 1 !important;
}
</style>
